import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface UseVoiceInputReturn {
  cancelRecording: () => void;
  error: string | null;
  isListening: boolean;
  isSupported: boolean;
  permission: boolean | null;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  transcript: string | null;
}

export function useVoiceInput(): UseVoiceInputReturn {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [permission, setPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const transcript = null; // Transcription is handled by the parent component

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Array<Blob>>([]);

  // Helper: Request Office Device Permission
  const requestOfficePermission = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      Office?.devicePermission?.requestPermissionsAsync(
        [Office.DevicePermissionType.microphone],
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            if (result.value === true) {
              console.log('VoiceInput: Office permission granted, reloading...');
              window.location.reload();
              return;
            }
            resolve();
          } else {
            reject(new Error(result.error?.message || 'Office permission denied'));
          }
        },
      );
    });
  }, []);

  // Helper: Open Fallback Dialog for Permission Bootstrapping
  const openPermissionDialog = useCallback(async () => {
    const url = `${window.location.origin}/taskpane.html?page=recorder&mode=permission`;

    return new Promise<void>((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        url,
        { displayInIframe: false, height: 30, width: 25 },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            toast.error(
              t('assistant.voiceInput.permissionDialogFailed', {
                message: asyncResult.error.message,
              }),
            );
            setError(asyncResult.error.message);
            reject(asyncResult.error);
          } else {
            const dialog = asyncResult.value;
            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              (arg: { message: string; origin: string | undefined } | { error: number }) => {
                try {
                  if ('message' in arg) {
                    const message = JSON.parse(arg.message);
                    if (message.type === 'PERMISSION_GRANTED') {
                      toast.success(t('assistant.voiceInput.permissionGranted'));
                      dialog.close();
                      resolve();
                    } else if (message.type === 'ERROR') {
                      toast.error(
                        t('assistant.voiceInput.permissionDeniedDetailed', {
                          error: message.error ?? '',
                        }),
                      );
                      setError(message.error ?? t('assistant.voiceInput.permissionDeniedShort'));
                      dialog.close();
                      reject(new Error(message.error));
                    }
                  } else if ('error' in arg) {
                    toast.error(
                      t('assistant.voiceInput.dialogError', {
                        error: String(arg.error),
                      }),
                    );
                    setError(
                      t('assistant.voiceInput.dialogError', {
                        error: String(arg.error),
                      }),
                    );
                    dialog.close();
                    reject(new Error('Dialog error: ' + arg.error));
                  }
                } catch (error) {
                  console.error('VoiceInput: Error parsing dialog message', error);
                }
              },
            );
          }
        },
      );
    });
  }, [t]);

  // Helper: Start Browser MediaRecorder
  const startBrowserRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setPermission(true);

    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.start();
    setIsListening(true);
  }, []);

  const startRecording = useCallback(async () => {
    console.log('VoiceInput: Starting process...');
    try {
      setError(null);

      if (window.location.pathname === '/recorder') {
        await startBrowserRecording();
        return;
      }

      // 1. Optimistic Attempt: Try direct recording in parent
      try {
        await startBrowserRecording();
        return;
      } catch (error: unknown) {
        const errorName = (error as Error).name;
        if (errorName === 'NotFoundError') {
          toast.error(t('assistant.voiceInput.noMic'));
          setError('no-mic');
          return;
        }
        console.log('VoiceInput: Direct access failed, bootstrapping...');
      }

      // 2. Bootstrap: Try Office API or Fallback Dialog
      if (Office?.devicePermission) {
        try {
          await requestOfficePermission();
        } catch {
          await openPermissionDialog();
        }
      } else {
        await openPermissionDialog();
      }

      // 3. Final Attempt: Trigger recording now that permission is granted
      await startBrowserRecording();
    } catch (error) {
      console.error('VoiceInput: Start failed:', error);
      toast.error(t('assistant.voiceInput.startFailed'));
      setPermission(false);
      setError('not-allowed');
      setIsListening(false);
    }
  }, [requestOfficePermission, openPermissionDialog, startBrowserRecording, t]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/mp3' });
        chunksRef.current = [];
        setIsListening(false);
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
        resolve(blob);
      };

      mediaRecorder.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    }
    chunksRef.current = [];
    setIsListening(false);
    setError(null);
  }, []);

  return {
    cancelRecording,
    error,
    isListening,
    isSupported: !!navigator.mediaDevices?.getUserMedia,
    permission,
    startRecording,
    stopRecording,
    transcript,
  };
}
